/**
 * TELA 03 — PRODUTO / OPÇÕES (Saneado)
 * Configuração estrita de opções comprovadas nas fontes canônicas.
 */

import { BURGER_ADDONS, PIZZA_EDGES } from '../data/menuData.js';
import { formatCurrency, formatPriceDisplay } from '../utils/formatters.js';

export function renderProductView(container, store) {
  const product = store.activeProduct;
  if (!product) {
    store.setView('home');
    return;
  }

  let quantity = 1;
  let selectedSize = 'media';
  let selectedEdge = null;
  let portionType = 'inteira';
  let selectedFlavor = product.flavors ? product.flavors[0] : null;
  let selectedAddons = [];
  let notes = '';

  if (store.editingCartItemId) {
    const existing = store.cart.find(i => i.cartItemId === store.editingCartItemId);
    if (existing) {
      quantity = existing.quantity || 1;
      if (existing.sizeKey) selectedSize = existing.sizeKey;
      if (existing.selectedEdgeId) selectedEdge = existing.selectedEdgeId;
      if (existing.portionType) portionType = existing.portionType;
      if (existing.flavor) selectedFlavor = existing.flavor;
      if (existing.selectedAddons) selectedAddons = [...existing.selectedAddons];
      if (existing.notes) notes = existing.notes;
    }
  }

  function calculateUnitPrice() {
    if (product.pricePending) return null;

    let base = 0;
    if (product.prices) {
      base = product.prices[selectedSize] || 0;
      if (selectedEdge) {
        const edgeObj = PIZZA_EDGES.find(e => e.id === selectedEdge);
        if (edgeObj && edgeObj.prices[selectedSize]) {
          base += edgeObj.prices[selectedSize];
        }
      }
    } else if (product.supportsHalf && portionType === 'meia') {
      base = Number((product.price * 0.6).toFixed(2));
    } else {
      base = product.price || 0;
    }

    selectedAddons.forEach(a => {
      base += a.price;
    });

    return Number(base.toFixed(2));
  }

  function calculateTotalPrice() {
    const unit = calculateUnitPrice();
    if (unit === null) return null;
    return Number((unit * quantity).toFixed(2));
  }

  function updateView() {
    const totalPrice = calculateTotalPrice();

    const priceDisplayEl = container.querySelector('#product-total-display');
    if (priceDisplayEl) {
      if (product.pricePending) {
        priceDisplayEl.textContent = formatPriceDisplay(null, true, product.priceLabel);
      } else {
        priceDisplayEl.textContent = formatCurrency(totalPrice);
      }
    }

    const qtyInput = container.querySelector('#product-qty-val');
    if (qtyInput) qtyInput.value = quantity;
  }

  container.innerHTML = `
    <div class="custom-panel" style="max-width: 680px; margin: 20px auto;">
      <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px;">
        <button id="btn-back-prev" class="btn btn-secondary btn-sm">
          <span>←</span> <span>Voltar</span>
        </button>
        <span class="badge badge-gold">${store.editingCartItemId ? 'Editando Item' : 'Novo Item'}</span>
      </div>

      <div class="panel-header">
        <h1 style="font-size: 1.5rem; font-weight: 800; color: var(--text-primary);">${product.name}</h1>
        <p style="font-size: 0.9rem; color: var(--text-muted); margin-top: 6px;">${product.description || ''}</p>
        ${product.hasPendingNote ? `
          <div style="margin-top: 10px; padding: 8px 12px; background: rgba(245, 158, 11, 0.1); border-left: 3px solid var(--accent-gold); font-size: 0.82rem; color: #fbbf24;">
            ⚠️ <em>Nota de cardápio:</em> Inconsistência na descrição original mantida sem invenção de dados.
          </div>
        ` : ''}
      </div>

      <!-- Configuração de Pizzas: Tamanho e Borda -->
      ${product.prices ? `
        <div class="step-card">
          <div class="step-header">
            <span class="step-title"><span class="step-badge">1</span> Escolha o Tamanho</span>
            <span class="badge badge-fire">Obrigatório</span>
          </div>

          <div class="radio-group" id="size-options">
            <div class="radio-card ${selectedSize === 'broto' ? 'active' : ''}" data-size="broto">
              <span class="radio-indicator"></span>
              <div>
                <strong style="display: block; font-size: 0.95rem;">Broto</strong>
                <span style="font-size: 0.85rem; color: var(--accent-gold-light);">${formatCurrency(product.prices.broto)}</span>
              </div>
            </div>

            <div class="radio-card ${selectedSize === 'media' ? 'active' : ''}" data-size="media">
              <span class="radio-indicator"></span>
              <div>
                <strong style="display: block; font-size: 0.95rem;">Méd. (Média)</strong>
                <span style="font-size: 0.85rem; color: var(--accent-gold-light);">${formatCurrency(product.prices.media)}</span>
              </div>
            </div>

            <div class="radio-card ${selectedSize === 'familia' ? 'active' : ''}" data-size="familia">
              <span class="radio-indicator"></span>
              <div>
                <strong style="display: block; font-size: 0.95rem;">Fam. (Família)</strong>
                <span style="font-size: 0.85rem; color: var(--accent-gold-light);">${formatCurrency(product.prices.familia)}</span>
              </div>
            </div>

            <div class="radio-card ${selectedSize === 'mega' ? 'active' : ''}" data-size="mega">
              <span class="radio-indicator"></span>
              <div>
                <strong style="display: block; font-size: 0.95rem;">Mega</strong>
                <span style="font-size: 0.85rem; color: var(--accent-gold-light);">${formatCurrency(product.prices.mega)}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="step-card">
          <div class="step-header">
            <span class="step-title"><span class="step-badge">2</span> Borda Recheada</span>
            <span class="badge badge-gold">Opcional</span>
          </div>

          <div class="radio-group" id="edge-options">
            <div class="radio-card ${selectedEdge === null ? 'active' : ''}" data-edge="none">
              <span class="radio-indicator"></span>
              <span>Sem borda</span>
            </div>
            ${PIZZA_EDGES.map(edge => `
              <div class="radio-card ${selectedEdge === edge.id ? 'active' : ''}" data-edge="${edge.id}">
                <span class="radio-indicator"></span>
                <div>
                  <strong>${edge.name}</strong>
                  <span style="display: block; font-size: 0.8rem; color: var(--accent-gold-light);" class="edge-price-${edge.id}">
                    +${formatCurrency(edge.prices[selectedSize])}
                  </span>
                </div>
              </div>
            `).join('')}
          </div>
        </div>
      ` : ''}

      <!-- Configuração de Calzone: Sabor -->
      ${product.isCalzone ? `
        <div class="step-card">
          <div class="step-header">
            <span class="step-title"><span class="step-badge">1</span> Escolha o Sabor do Calzone</span>
            <span class="badge badge-fire">Obrigatório</span>
          </div>

          <div class="radio-group" id="flavor-options">
            ${product.flavors.map(flavor => `
              <div class="radio-card ${selectedFlavor === flavor ? 'active' : ''}" data-flavor="${flavor}">
                <span class="radio-indicator"></span>
                <span>${flavor}</span>
              </div>
            `).join('')}
          </div>
        </div>
      ` : ''}

      <!-- Configuração de Porções: Inteira ou Meia (Página 5) -->
      ${product.supportsHalf ? `
        <div class="step-card">
          <div class="step-header">
            <span class="step-title"><span class="step-badge">1</span> Tamanho da Porção</span>
            <span class="badge badge-fire">Obrigatório</span>
          </div>

          <div class="radio-group" id="portion-options">
            <div class="radio-card ${portionType === 'inteira' ? 'active' : ''}" data-portion="inteira">
              <span class="radio-indicator"></span>
              <div>
                <strong>Porção Inteira</strong>
                <span style="display: block; font-size: 0.85rem; color: var(--accent-gold-light);">${formatCurrency(product.price)}</span>
              </div>
            </div>

            <div class="radio-card ${portionType === 'meia' ? 'active' : ''}" data-portion="meia">
              <span class="radio-indicator"></span>
              <div>
                <strong>Meia Porção (60% da inteira)</strong>
                <span style="display: block; font-size: 0.85rem; color: var(--accent-gold-light);">${formatCurrency(product.price * 0.6)}</span>
              </div>
            </div>
          </div>
        </div>
      ` : ''}

      <!-- Adicionais Permitidos de Lanches (Página 8) -->
      ${product.allowBurgerAddons ? `
        <div class="step-card">
          <div class="step-header">
            <span class="step-title"><span class="step-badge">+</span> Adicionais de Lanches</span>
            <span class="badge badge-gold">Opcional</span>
          </div>

          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 8px;" id="addons-list">
            ${BURGER_ADDONS.map(addon => {
              const isChecked = selectedAddons.some(a => a.id === addon.id);
              return `
                <label style="background: var(--bg-surface); border: 1px solid var(--border-card); border-radius: var(--radius-md); padding: 10px 12px; display: flex; align-items: center; justify-content: space-between; cursor: pointer; user-select: none;">
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <input type="checkbox" class="addon-checkbox" value="${addon.id}" ${isChecked ? 'checked' : ''} style="width: 18px; height: 18px; accent-color: var(--accent-gold);" />
                    <span style="font-size: 0.9rem;">${addon.name}</span>
                  </div>
                  <strong style="color: var(--accent-gold-light); font-size: 0.85rem;">+${formatCurrency(addon.price)}</strong>
                </label>
              `;
            }).join('')}
          </div>
        </div>
      ` : ''}

      <!-- Observações -->
      <div class="step-card">
        <div class="step-header">
          <span class="step-title">Observações do Item</span>
        </div>
        <textarea 
          id="item-notes" 
          class="form-control" 
          rows="2" 
          placeholder="Ex: sem cebola, ponto da carne, etc..."
        >${notes}</textarea>
      </div>

      <!-- Rodapé com Quantidade e Total -->
      <div style="display: flex; flex-direction: column; gap: 14px; margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--border-subtle);">
        <div style="display: flex; align-items: center; justify-content: space-between;">
          <div style="display: flex; align-items: center; gap: 12px;">
            <span style="font-size: 0.95rem; font-weight: 600; color: var(--text-secondary);">Quantidade:</span>
            <div class="qty-control">
              <button id="btn-qty-minus" class="qty-btn">−</button>
              <input type="text" id="product-qty-val" class="qty-input" value="${quantity}" readonly />
              <button id="btn-qty-plus" class="qty-btn">+</button>
            </div>
          </div>

          <div style="text-align: right;">
            <span style="font-size: 0.75rem; color: var(--text-muted); display: block; text-transform: uppercase;">Total do Item</span>
            <span id="product-total-display" style="font-size: 1.4rem; font-weight: 800; color: var(--accent-gold-light); font-family: var(--font-display);">
              ${product.pricePending ? formatPriceDisplay(null, true, product.priceLabel) : formatCurrency(calculateTotalPrice())}
            </span>
          </div>
        </div>

        <button id="btn-confirm-add" class="btn btn-primary btn-block" style="padding: 14px; font-size: 1.05rem;">
          ${store.editingCartItemId ? 'Salvar Alterações no Carrinho' : 'Adicionar ao Carrinho'}
        </button>
      </div>
    </div>
  `;

  // Listeners de Tamanho de Pizza
  container.querySelectorAll('#size-options .radio-card').forEach(card => {
    card.addEventListener('click', () => {
      container.querySelectorAll('#size-options .radio-card').forEach(c => c.classList.remove('active'));
      card.classList.add('active');
      selectedSize = card.getAttribute('data-size');
      
      PIZZA_EDGES.forEach(edge => {
        const el = container.querySelector(`.edge-price-${edge.id}`);
        if (el) el.textContent = `+${formatCurrency(edge.prices[selectedSize])}`;
      });

      updateView();
    });
  });

  // Listeners de Borda de Pizza
  container.querySelectorAll('#edge-options .radio-card').forEach(card => {
    card.addEventListener('click', () => {
      container.querySelectorAll('#edge-options .radio-card').forEach(c => c.classList.remove('active'));
      card.classList.add('active');
      const edge = card.getAttribute('data-edge');
      selectedEdge = edge === 'none' ? null : edge;
      updateView();
    });
  });

  // Listeners de Calzone
  container.querySelectorAll('#flavor-options .radio-card').forEach(card => {
    card.addEventListener('click', () => {
      container.querySelectorAll('#flavor-options .radio-card').forEach(c => c.classList.remove('active'));
      card.classList.add('active');
      selectedFlavor = card.getAttribute('data-flavor');
      updateView();
    });
  });

  // Listeners de Porções
  container.querySelectorAll('#portion-options .radio-card').forEach(card => {
    card.addEventListener('click', () => {
      container.querySelectorAll('#portion-options .radio-card').forEach(c => c.classList.remove('active'));
      card.classList.add('active');
      portionType = card.getAttribute('data-portion');
      updateView();
    });
  });

  // Checkboxes de Adicionais
  container.querySelectorAll('.addon-checkbox').forEach(chk => {
    chk.addEventListener('change', (e) => {
      const addonId = e.target.value;
      const addonObj = BURGER_ADDONS.find(a => a.id === addonId);
      if (e.target.checked) {
        if (addonObj && !selectedAddons.some(a => a.id === addonId)) {
          selectedAddons.push(addonObj);
        }
      } else {
        selectedAddons = selectedAddons.filter(a => a.id !== addonId);
      }
      updateView();
    });
  });

  // Quantidade
  container.querySelector('#btn-qty-minus')?.addEventListener('click', () => {
    if (quantity > 1) {
      quantity--;
      updateView();
    }
  });

  container.querySelector('#btn-qty-plus')?.addEventListener('click', () => {
    quantity++;
    updateView();
  });

  // Voltar
  container.querySelector('#btn-back-prev')?.addEventListener('click', () => {
    if (store.editingCartItemId) {
      store.setView('cart');
    } else {
      store.setView('category', { categoryId: product.categoryId });
    }
  });

  // Adicionar / Salvar
  container.querySelector('#btn-confirm-add')?.addEventListener('click', () => {
    const finalNotes = container.querySelector('#item-notes')?.value || '';
    const unitPrice = calculateUnitPrice();
    const totalPrice = calculateTotalPrice();

    let sizeName = null;
    if (product.prices) {
      const sizeNames = { broto: "Broto", media: "Méd.", familia: "Fam.", mega: "Mega" };
      sizeName = sizeNames[selectedSize];
    }

    let selectedEdgeName = null;
    if (selectedEdge) {
      const edgeObj = PIZZA_EDGES.find(e => e.id === selectedEdge);
      if (edgeObj) selectedEdgeName = edgeObj.name;
    }

    const cartItem = {
      productId: product.id,
      name: product.name,
      quantity,
      unitPrice,
      totalPrice,
      pricePending: !!product.pricePending,
      sizeKey: product.prices ? selectedSize : null,
      sizeName,
      selectedEdgeId: selectedEdge,
      selectedEdge: selectedEdgeName,
      flavor: product.isCalzone ? selectedFlavor : null,
      portionType: product.supportsHalf ? portionType : null,
      selectedAddons: [...selectedAddons],
      notes: finalNotes
    };

    store.addToCart(cartItem);
    store.setView('cart');
  });
}
